import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as CloseIcon } from "../../../assets/images/icons/close.svg";

type Props = {
    title: string;
    description: string | null;
    triggerId: string;
    id: string;
    positionX?: 'left' | 'right' | 'center';
    positionY?: 'top' | 'bottom' | 'center';
    mobilePositionX?: 'left' | 'right' | 'center';
    mobilePositionY?: 'top' | 'bottom' | 'center';
    isModal?: boolean; // Aggiunta prop opzionale per il comportamento modale nei DocumentsTable
};

const Tooltip: React.FC<Props> = ({
    title,
    description,
    triggerId,
    id,
    positionX = 'left',
    positionY = 'bottom',
    mobilePositionX = 'left',
    mobilePositionY = 'bottom',
    isModal = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isPositionRight, setIsPositionRight] = useState(true);

    useEffect(() => {
        const tooltip = document.getElementById(id);
        const trigger = document.getElementById(triggerId);

        const updatePosition = () => {
            if (trigger) {
                const rect = trigger.getBoundingClientRect();
                const screenWidth = window.innerWidth;
                const tooltipWidth = 240;

                const shouldOpenLeft = rect.right + tooltipWidth + 30 > screenWidth;
                setIsPositionRight(!shouldOpenLeft);

                setPosition({
                    top: rect.top + window.scrollY,  // Offset sopra l’elemento
                    left: shouldOpenLeft
                        ? rect.left - window.scrollX - 50 // Offset a sinistra
                        : rect.right + window.scrollX + 50 // Offset a destra
                });
            }
        };

        const triggerClickListener = () => {
            if (isOpen === false) {
                updatePosition(); // Calcola la posizione quando si apre
                open();
            } else {
                close();
            }
        };

        const documentClickListener = (e: any) => {
            if (trigger && tooltip) {
                if (!trigger.contains(e.target) && !tooltip?.contains(e.target)) {
                    close();
                }
            }
        };

        if (trigger) {
            trigger.addEventListener('click', triggerClickListener);
        }

        document.addEventListener('click', documentClickListener);
        window.addEventListener("resize", updatePosition);

        return () => {
            document.removeEventListener('click', documentClickListener);
            window.removeEventListener("resize", updatePosition);
            if (trigger) {
                trigger?.removeEventListener('click', triggerClickListener);
            }
        };

    }, [isOpen, triggerId, id]);

    const open = () => {
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
    };

    const tooltipContent = (
        <div
            id={id}
            className="fixed p-4 w-max max-w-[240px] bg-white z-50 border border-red rounded-[8px]"
            style={{
                top: position.top,
                left: position.left,
                transform: "translate(-50%, -100%)", // Per centrare rispetto al trigger
            }}
        >
            <div className="flex items-center justify-between mb-[17px]">
                <h5 className="leading-[19px] mr-2 font-semibold text-black normal-case text-base">{title}</h5>
                <div onClick={() => close()} className="mt-[3px] cursor-pointer">
                    <CloseIcon />
                </div>
            </div>
            {!!description && (
                <p dangerouslySetInnerHTML={{ __html: description }} className="text-sm text-black font-normal normal-case whitespace-normal"></p>
            )}
        </div>
    );

    // Se `isModal` è true, usa createPortal per renderizzare nel body
    return isOpen ? (isModal ? createPortal(tooltipContent, document.body) : tooltipContent) : null;
};

export default Tooltip;
